'use client';

import { Analytics } from '@vercel/analytics/react';
import { useTranslation } from 'react-i18next';

import { MarketingFooter } from '~/(marketing)/_components/marketing-footer';
import { MarketingHeader } from '~/(marketing)/_components/marketing-header';
import { SectionLabel } from '~/(marketing)/_components/marketing-sections';
import { asMarketingLocale } from '~/lib/marketing/site';

import '~/(marketing)/_components/marketing.css';

const LEGAL_LABEL = { en: 'Legal', ar: 'الشؤون القانونية' } as const;

/**
 * Apex (askflow.sa) chrome for the platform legal pages: the same header,
 * ink hero, bone body and footer as the marketing site. Tenant hosts never
 * reach this; they render their own legal text inside the tenant layout.
 */
export function MarketingLegalShell({
  title,
  lastUpdated,
  children,
}: React.PropsWithChildren<{ title: string; lastUpdated: string }>) {
  const { i18n } = useTranslation();
  const locale = asMarketingLocale(i18n.language);

  return (
    <div
      className="min-h-screen bg-[#f7f6f4]"
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
    >
      <MarketingHeader />

      <section className="relative overflow-hidden bg-[#202020] px-4 pb-14 pt-[72px] text-white md:px-8 md:pb-20 md:pt-[96px] lg:px-[90px]">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-48 end-[-10%] h-[680px] w-[680px] rounded-full bg-[#a9c8ff]/[0.08] blur-[140px]"
        />
        <div className="relative mx-auto max-w-[820px]">
          <SectionLabel tone="light">{LEGAL_LABEL[locale]}</SectionLabel>
          <h1
            className="font-roboto text-[32px] leading-[1.06] tracking-tight md:text-[48px] lg:text-[56px]"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            {title}
          </h1>
          <p className="mt-6 font-roboto-mono text-[12px] uppercase tracking-wide text-white/55 md:text-[13px]">
            {lastUpdated}
          </p>
        </div>
      </section>

      <main className="px-4 py-12 md:px-8 md:py-16 lg:px-[90px] lg:py-20">
        <article className="mx-auto max-w-[820px] font-roboto text-[16px] text-[#202020] md:text-[17px]">
          {children}
        </article>
      </main>

      <MarketingFooter />

      <Analytics />
    </div>
  );
}
